import { Link, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Image from 'src/components/image/image';
import { fDate } from 'src/utils/format_time';
import { useProductName } from 'src/hooks/useProductName';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Tooltip, Icon } from '@shopify/polaris';
import { BarcodeIcon } from '@shopify/polaris-icons';

const ellipsisSx = {
  display: 'block',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const useOverflow = () => {
  const [overflowing, setOverflowing] = useState(false);
  const observerRef = useRef(null);

  const ref = useCallback((node) => {
    observerRef.current?.disconnect();
    if (!node) return;

    const measure = () =>
      setOverflowing(node.scrollHeight > node.clientHeight || node.scrollWidth > node.clientWidth);
    measure();

    observerRef.current = new ResizeObserver(measure);
    observerRef.current.observe(node);
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return [ref, overflowing];
};

export const ProductViewType = {
  ROW_SMALL: 'row_small',
  ROW_SMALL_LINK: 'row_small_link',
  ROW_DETAILED: 'row_detailed',
  QUANTITY: 'quantity',
  CHANGE: 'change',
};

export const ProductCard = ({
  product,
  productViewType,
  onClick,
  titleOnClick = null,
  shipped = false,
  showBarcode = false,
  barcodeIcon = false,
  tooltip = false,
  alignItems = 'center',
  badge = null,
  option = null,
  lotNumber = null,
  expirationDt = null,
  stockTypeBadge = null,
}) => {
  const { t } = useTranslation();
  const productName = useProductName(product?.productNameLocal, product?.productNameEnglish);

  const [productRef, showProductTooltip] = useOverflow();
  const [optionRef, showOptionTooltip] = useOverflow();

  var {
    productId,
    sku,
    representativeImageUrl,
    productRepresentativeImageUrl,
    orderQuantity = 0,
    slot,
    stockQuantity,
    productImageUrl,
    productBarcode,
    barcode,
  } = product;
  representativeImageUrl =
    representativeImageUrl || productImageUrl || productRepresentativeImageUrl;
  if (
    productViewType === ProductViewType.ROW_SMALL ||
    productViewType === ProductViewType.ROW_SMALL_LINK
  ) {
    const link = productViewType === ProductViewType.ROW_SMALL_LINK;
    return (
      <div onClick={onClick}>
        <Stack
          direction={'row'}
          textAlign={'start'}
          alignItems={alignItems}
          spacing={1.5}
        >
          <Image
            src={representativeImageUrl}
            alt={productId}
            sx={{
              width: 40,
              height: 40,
              borderRadius: 'var(--p-border-radius-200)',
              flexShrink: 0,
            }}
          />

          <Stack
            direction={'column'}
            textAlign={'start'}
            sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
          >
            <Stack
              direction={'row'}
              alignItems={'center'}
              spacing={0.5}
              sx={{ minWidth: 0 }}
            >
              {link ? (
                <Link
                  underline="hover"
                  sx={{ minWidth: 0 }}
                >
                  <Typography
                    ref={productRef}
                    onClick={titleOnClick}
                    variant="body_md_medium"
                    sx={ellipsisSx}
                  >
                    {showProductTooltip ? (
                      <Tooltip content={productName}>
                        <span>{productName}</span>
                      </Tooltip>
                    ) : (
                      productName
                    )}
                  </Typography>
                </Link>
              ) : tooltip ? (
                <Typography
                  variant="body_md_semibold"
                  sx={{ minWidth: 0 }}
                >
                  {showProductTooltip ? (
                    <Tooltip content={productName}>
                      <div
                        className="tooltip-cell-text one-line"
                        ref={productRef}
                      >
                        {productName}
                      </div>
                    </Tooltip>
                  ) : (
                    <p
                      ref={productRef}
                      className="tooltip-cell-text one-line"
                    >
                      {productName}
                    </p>
                  )}
                </Typography>
              ) : (
                <Typography variant="body_md_medium">{productName}</Typography>
              )}
              {badge}
            </Stack>

            <Typography variant={'body_md'}>{`SKU: ${sku}`}</Typography>
            {slot && <Typography variant={'body_md'}>{`Slot: ${slot}`}</Typography>}
            {(shipped || showBarcode) &&
              (barcodeIcon ? (
                <Stack
                  direction={'row'}
                  alignItems={'center'}
                  spacing={0.5}
                >
                  <Icon source={BarcodeIcon} />
                  <Typography variant={'body_md'}>
                    {productBarcode ? productBarcode : barcode ? barcode : ''}
                  </Typography>
                </Stack>
              ) : (
                <Typography
                  variant={'body_md'}
                >{`${t('common:field.barcode')}: ${productBarcode ? productBarcode : barcode ? barcode : ''}`}</Typography>
              ))}
            {option && (
              <Typography
                ref={optionRef}
                variant={'body_md'}
                sx={ellipsisSx}
              >
                {showOptionTooltip ? (
                  <Tooltip content={`Option: ${option}`}>
                    <span>{`Option: ${option}`}</span>
                  </Tooltip>
                ) : (
                  `Option: ${option}`
                )}
              </Typography>
            )}
            {lotNumber && <Typography variant={'body_md'}>{`Lot: ${lotNumber}`}</Typography>}
            {expirationDt && (
              <Typography variant={'body_md'}>{`Exp: ${fDate(expirationDt)}`}</Typography>
            )}
            {stockTypeBadge && (
              <Stack
                direction={'row'}
                alignItems={'center'}
                spacing={0.5}
                sx={{ mt: 0.5 }}
              >
                <Typography variant={'body_md'}>Inventory type:</Typography>
                {stockTypeBadge}
              </Stack>
            )}
          </Stack>
        </Stack>
      </div>
    );
  }

  if (productViewType === ProductViewType.ROW_DETAILED) {
    return (
      <Stack
        direction={'row'}
        textAlign={'start'}
        alignItems={'center'}
        justifyContent={'space-between'}
        spacing={1}
      >
        <Stack
          direction={'row'}
          spacing={1.5}
        >
          <Image
            src={representativeImageUrl}
            alt={productId}
            sx={{
              width: 40,
              height: 40,
              borderRadius: 'var(--p-border-radius-200)',
            }}
          />
          <Stack
            direction={'column'}
            textAlign={'start'}
          >
            <Typography variant="body_md_medium">{productName}</Typography>
            <Typography variant={'body_md'}>{`SKU: ${sku}`}</Typography>
          </Stack>
        </Stack>
        {orderQuantity}
      </Stack>
    );
  }

  if (productViewType === ProductViewType.QUANTITY) {
    return (
      <Stack
        direction={'row'}
        textAlign={'start'}
        alignItems={'center'}
        justifyContent={'space-between'}
        spacing={1}
      >
        <Stack
          direction={'column'}
          textAlign={'start'}
        >
          <Typography variant="body_md_medium">{productName}</Typography>
          <Typography variant={'body_md'}>{`SKU: ${sku}`}</Typography>
          <Typography variant="body_md">{`${t('common:field.on_hand')}: ${stockQuantity}`}</Typography>
        </Stack>
      </Stack>
    );
  }

  if (productViewType === ProductViewType.CHANGE) {
    return (
      <Stack
        direction={'row'}
        textAlign={'start'}
        alignItems={'center'}
        justifyContent={'space-between'}
        spacing={1}
      >
        <Stack
          direction={'column'}
          textAlign={'start'}
        >
          <Typography variant="body_md_medium">{productName}</Typography>
          <Typography variant={'body_md'}>{`SKU: ${sku}`}</Typography>
        </Stack>
      </Stack>
    );
  }
};
